import { Routes, Route, Navigate } from 'react-router-dom';

import { ProtectedRoute } from './auth/ProtectedRoute.jsx';
import { useAdminAuth } from './auth/AdminAuthContext.jsx';
import { Layout } from './layout/Layout.jsx';
import { Login } from './pages/Login.jsx';
import { Overview } from './pages/Overview.jsx';
import { SupportOverview } from './pages/SupportOverview.jsx';
import { Users } from './pages/Users.jsx';
import { Queries } from './pages/Queries.jsx';
import { Plans } from './pages/Plans.jsx';
import { Broadcasts } from './pages/Broadcasts.jsx';
import { Map } from './pages/Map.jsx';
import { Finances } from './pages/Finances.jsx';
import { Admins } from './pages/Admins.jsx';
import { Settings } from './pages/Settings.jsx';
import { AuditLogs } from './pages/AuditLogs.jsx';
import { Placeholder } from './pages/Placeholder.jsx';

/**
 * Dispatches the `/` route to the right Overview component based on
 * the signed-in admin's role. Superadmins see the finance-heavy
 * Overview (MRR, conversion, signups, all admin actions); support
 * roles see SupportOverview (queue KPIs + a volume chart + support-
 * team actions only).
 *
 * Why a dispatcher component rather than two separate routes? The
 * URL stays `/` for both — bookmarks, browser history, and the
 * sidebar's NavLink "active" matching all keep working without
 * conditional routing logic.
 */
function HomeOverview() {
  const { me } = useAdminAuth();
  return me?.role === 'SUPERADMIN' ? <Overview /> : <SupportOverview />;
}

/**
 * Gate a route to Superadmin only. Non-Superadmins who arrive via
 * deep link (the sidebar already hides the entry for them) get
 * bounced to `/` rather than rendering a broken page that would
 * just 403-toast on every API call.
 *
 * Auth status is still in flight on initial cold-load — render
 * nothing rather than briefly redirecting away from a page the
 * user is in fact allowed to see. The ProtectedRoute wrapper above
 * already handles the "not signed in at all" branch.
 */
function SuperadminOnly({ children }) {
  const { me, status } = useAdminAuth();
  if (status !== 'authed') return null;
  if (me?.role !== 'SUPERADMIN') return <Navigate to="/" replace />;
  return children;
}

/**
 * Root router. The Login route is public; everything else is wrapped
 * in <ProtectedRoute> which checks for a valid admin JWT and bounces
 * to /login on 401.
 *
 * The non-Overview pages are intentionally stubbed with
 * <Placeholder>. The migration plan is to port them one at a time —
 * until then the route exists so the sidebar renders without dead
 * links.
 *
 * @author <lukman@zshift.io>
 */
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/"          element={<HomeOverview />} />
        <Route path="/users"     element={<Users />} />
        <Route path="/queries"   element={<Queries />} />
        <Route path="/plans"     element={<Plans />} />
        <Route path="/broadcasts" element={<Broadcasts />} />
        <Route path="/finances"  element={<Finances />} />
        <Route path="/map"       element={<Map />} />
        <Route path="/admins"    element={<Admins />} />
        <Route path="/logs"      element={<SuperadminOnly><AuditLogs /></SuperadminOnly>} />
        <Route path="/settings"  element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
